import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { BehaviorSubject, Observable, of, switchMap, throwError } from "rxjs";

import { jwtDecode } from "jwt-decode";
import { catchError } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { ApiResponse } from "../../../shared/models/api-response.interface";
import {
  ChangePasswordRequest,
  ChangePasswordResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  JwtPayload,
  LoginRequest,
  LoginRespone,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ResendOtpRequest,
  ResendOtpResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  User,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from "../../../shared/models/user.interface";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  apiUrl!: string;
  apiV!: string;
  private userEmail = new BehaviorSubject<string | null>(null);
  userEmail$ = this.userEmail.asObservable();
  private tokenKey = "authToken";
  private loggedIn = new BehaviorSubject<boolean>(this.hasValidToken());
  constructor(private httpClient: HttpClient) {
    this.apiUrl = environment.apiURL;
    this.apiV = environment.version;
    if (!this.hasValidToken()) {
      this.removeToken();
    }
  }

  login(loginRequest: LoginRequest): Observable<ApiResponse<LoginRespone>> {
    return this.httpClient
      .post<
        ApiResponse<LoginRespone>
      >(this.apiUrl + `/api/${this.apiV}/Authentication/Login`, loginRequest)
      .pipe(
        switchMap((res: ApiResponse<LoginRespone>) => {
          if (res.success) {
            this.setToken(res.data.accessToken as string);
            const jwt: any = jwtDecode(res.data.accessToken);
            if (jwt?.sub) {
              localStorage.setItem("userId", jwt.sub);
            }

            // Persist userName for profile prefill: prefer JWT claims, fallback to login input
            const claimUserName =
              jwt?.unique_name ||
              jwt?.preferred_username ||
              jwt?.upn ||
              jwt?.email ||
              loginRequest.userName;
            if (claimUserName) {
              localStorage.setItem("email", String(claimUserName));
            }
          }
          return of(res);
        }),
      );
  }

  refreshToken(): Observable<ApiResponse<RefreshTokenResponse>> {
    const request: RefreshTokenRequest = {
      userName: "",
      refreshToken: this.getToken() ?? "",
    };
    return this.httpClient.post<ApiResponse<RefreshTokenResponse>>(
      this.apiUrl + `/api/${this.apiV}/Authentication/RefreshToken`,
      request,
    );
  }
  isLoggedInSync(): boolean {
    return this.loggedIn.value;
  }

  fetchUserData(userId: string) {
    return this.httpClient.get<ApiResponse<User>>(
      this.apiUrl + `/api/${this.apiV}/Users`,
      {
        params: { id: userId },
      },
    );
  }



  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    try {
      const jwt: any = jwtDecode(token);
      if (jwt?.sub) {
        localStorage.setItem("userId", jwt.sub);
      }
      const claimUserName =
        jwt?.unique_name ||
        jwt?.preferred_username ||
        jwt?.upn ||
        jwt?.email;
      if (claimUserName) {
        localStorage.setItem("email", String(claimUserName));
      }
    } catch (e) {}
    this.loggedIn.next(true);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  removeToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem("userId");
    localStorage.removeItem("email");
    localStorage.removeItem("userName");
    this.loggedIn.next(false);
  }

  logoutApi(): Observable<any> {
    return this.httpClient.post(
      `${this.apiUrl}/api/${this.apiV}/Authentication/Logout`,
      {},
      { withCredentials: true }
    );
  }

  isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const now = Math.floor(Date.now() / 1000);
      return decoded.exp < now;
    } catch {
      return true;
    }
  }

  hasValidToken(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  register(request: any): Observable<ApiResponse<any>> {
    return this.httpClient.post<ApiResponse<any>>(
      this.apiUrl + `/api/${this.apiV}/Users`,
      request,
    );
  }

  checkEmailExists(email: string): Observable<boolean> {
    return this.httpClient
      .get<ApiResponse<boolean>>(
        `${this.apiUrl}/api/${this.apiV}/EndUsers/check-email-exists`,
        {
          params: { email },
        },
      )
      .pipe(
        switchMap((res) => of(res.data as boolean)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(false);
          }
          return throwError(() => error);
        }),
      );
  }

  verifyOtp(
    request: VerifyOtpRequest,
  ): Observable<ApiResponse<VerifyOtpResponse>> {
    return this.httpClient
      .post<
        ApiResponse<VerifyOtpResponse>
      >(`${this.apiUrl}/api/${this.apiV}/Users/VerifyOtp`, request)
      .pipe(
        switchMap((res: ApiResponse<VerifyOtpResponse>) => {
          if (res.success && res.data?.accessToken) {
            this.setToken(res.data.accessToken);
            const jwt: any = jwtDecode(res.data.accessToken);
            if (jwt?.sub) {
              localStorage.setItem("userId", jwt.sub);
            }
            const claimUserName =
              jwt?.unique_name ||
              jwt?.preferred_username ||
              jwt?.upn ||
              jwt?.email ||
              request.email;
            if (claimUserName) {
              localStorage.setItem("userName", String(claimUserName));
            }
          }
          return of(res);
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  resendOtp(
    request: ResendOtpRequest,
  ): Observable<ApiResponse<ResendOtpResponse>> {
    return this.httpClient
      .post<
        ApiResponse<ResendOtpResponse>
      >(`${this.apiUrl}/api/${this.apiV}/Users/ResendOtp`, request)
      .pipe(
        switchMap((res: ApiResponse<ResendOtpResponse>) => of(res)),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  forgotPassword(
    request: ForgotPasswordRequest,
  ): Observable<ApiResponse<ForgotPasswordResponse>> {
    return this.httpClient
      .post<
        ApiResponse<ForgotPasswordResponse>
      >(`${this.apiUrl}/api/${this.apiV}/Authentication/ForgotPassword`, request)
      .pipe(
        switchMap((res) => of(res)),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  resetPassword(
    request: ResetPasswordRequest,
  ): Observable<ApiResponse<ResetPasswordResponse>> {
    return this.httpClient
      .post<
        ApiResponse<ResetPasswordResponse>
      >(`${this.apiUrl}/api/${this.apiV}/Authentication/ResetPassword`, request)
      .pipe(
        switchMap((res) => of(res)),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  changePassword(request: ChangePasswordRequest) {
    return this.httpClient.post<ApiResponse<ChangePasswordResponse>>(
      `${this.apiUrl}/api/${this.apiV}/Authentication/ChangePassword`,
      request,
    );
  }

  //Login with Social Start Code
  loginWithOAuth(
    provider: "Google" | "Facebook",
    returnUrl: string = "/",
  ): void {
    const callbackPath =
      provider === "Google"
        ? "/auth/google/callback"
        : "/auth/facebook/callback";
    const callbackUrl = `${window.location.origin}${callbackPath}?mainReturnUrl=${returnUrl}`;

    const authUrl = `${this.apiUrl}/api/${this.apiV}/Authentication/externallogin?provider=${provider}&returnUrl=${callbackUrl}`;

    const popup = window.open(authUrl, "oauth-popup", "width=500,height=600");
    if (!popup) {
      window.location.href = authUrl;
    }
  }

  getUserEmail(): string | null {
    return localStorage.getItem("email");
  }

  // Lấy UserId hiện tại cho google analytic
  getCurrentUserId(): string | null {
    const token = this.getToken();
    if (token) {
      try {
        const jwt: any = jwtDecode(token);
        return jwt?.sub || localStorage.getItem("userId");
      } catch {
        return null;
      }
    }
    return null;
  }
}
